import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Home, LogOut, RefreshCw, Filter, Plus, DollarSign,
  User, Calendar, Target, ChevronRight, Eye, Edit,
  ArrowRight, Phone, Mail, MapPin, Tag, TrendingUp,
  FileText, CheckCircle, XCircle, Send, Clock
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Labels et couleurs des stages
const STAGE_CONFIG = {
  lead: { label: 'Lead', color: '#64B5F6', bg: '#E3F2FD' },
  qualified: { label: 'Qualifié', color: '#4DB6AC', bg: '#E0F2F1' },
  estimate: { label: 'Soumission', color: '#FFB74D', bg: '#FFF3E0' },
  negotiation: { label: 'Négociation', color: '#BA68C8', bg: '#F3E5F5' },
  won: { label: 'Gagné', color: '#81C784', bg: '#E8F5E9' },
  production: { label: 'Production', color: '#4FC3F7', bg: '#E1F5FE' },
  delivered: { label: 'Livré', color: '#2E7D32', bg: '#C8E6C9' },
  lost: { label: 'Perdu', color: '#E57373', bg: '#FFEBEE' }
};

// Status des soumissions
const SUBMISSION_STATUS_CONFIG = {
  draft: { label: 'Brouillon', color: '#9E9E9E', bg: '#F5F5F5' },
  sent: { label: 'Envoyée', color: '#1976D2', bg: '#E3F2FD' },
  accepted: { label: 'Acceptée', color: '#2E7D32', bg: '#E8F5E9' },
  rejected: { label: 'Rejetée', color: '#C62828', bg: '#FFEBEE' },
  expired: { label: 'Expirée', color: '#F57C00', bg: '#FFF3E0' }
};

// Status des contrats
const CONTRACT_STATUS_CONFIG = {
  draft: { label: 'Brouillon', color: '#9E9E9E', bg: '#F5F5F5' },
  sent: { label: 'Envoyé', color: '#1976D2', bg: '#E3F2FD' },
  signed: { label: 'Signé', color: '#2E7D32', bg: '#E8F5E9' },
  in_production: { label: 'En production', color: '#7B1FA2', bg: '#F3E5F5' },
  delivered: { label: 'Livré', color: '#00695C', bg: '#E0F2F1' },
  closed: { label: 'Clôturé', color: '#37474F', bg: '#ECEFF1' },
  canceled: { label: 'Annulé', color: '#C62828', bg: '#FFEBEE' }
};

const STAGE_ORDER = ['lead', 'qualified', 'estimate', 'negotiation', 'won', 'production', 'delivered', 'lost'];

const Pipeline = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [pipelineData, setPipelineData] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ source: '', deal_type: '', score_min: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [showDealModal, setShowDealModal] = useState(false);
  const [dealSubmissions, setDealSubmissions] = useState([]);
  const [dealContract, setDealContract] = useState(null);
  const [activeTab, setActiveTab] = useState('info'); // info, submissions, contract

  // Auth
  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const verifyAuth = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/login');
      return;
    }
    try {
      const response = await axios.get(`${API}/auth/verify`, getAuthHeaders());
      setUser(response.data);
    } catch {
      localStorage.removeItem('admin_token');
      navigate('/login');
    }
  };

  const logout = () => {
    localStorage.removeItem('admin_token');
    navigate('/login');
  };

  // Data fetching
  const fetchPipeline = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/dashboard/pipeline-summary`, getAuthHeaders());
      setPipelineData(response.data.pipeline || {});
    } catch (error) {
      console.error('Erreur chargement pipeline:', error);
      toast.error('Erreur lors du chargement du pipeline');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    verifyAuth();
  }, []);

  useEffect(() => {
    if (user) {
      fetchPipeline();
    }
  }, [user]);

  // Actions
  const handleStageChange = async (dealId, newStage) => {
    if (newStage === 'lost') {
      const reason = window.prompt('Raison de la perte (obligatoire):');
      if (!reason) return;
      
      try {
        await axios.post(`${API}/deals/${dealId}/stage`, {
          new_stage: newStage,
          lost_reason: reason
        }, getAuthHeaders());
        toast.success('Deal marqué comme perdu');
        fetchPipeline();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erreur lors du changement de stage');
      }
    } else {
      try {
        await axios.post(`${API}/deals/${dealId}/stage`, {
          new_stage: newStage
        }, getAuthHeaders());
        toast.success(`Stage changé vers "${STAGE_CONFIG[newStage]?.label}"`);
        fetchPipeline();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erreur lors du changement de stage');
      }
    }
  };

  const openDealDetail = async (dealId) => {
    try {
      const response = await axios.get(`${API}/deals/${dealId}`, getAuthHeaders());
      setSelectedDeal(response.data);
      setShowDealModal(true);
      setActiveTab('info');
      
      // Charger les soumissions du deal
      try {
        const subsResponse = await axios.get(`${API}/deals/${dealId}/submissions`, getAuthHeaders());
        setDealSubmissions(subsResponse.data.submissions || []);
      } catch {
        setDealSubmissions([]);
      }
      
      // Charger le contrat du deal
      try {
        const contractResponse = await axios.get(`${API}/deals/${dealId}/contract`, getAuthHeaders());
        setDealContract(contractResponse.data.contract || null);
      } catch {
        setDealContract(null);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement du deal');
    }
  };
  
  // Actions sur les soumissions
  const handleMarkSubmissionSent = async (submissionId) => {
    try {
      await axios.post(`${API}/submissions/${submissionId}/mark-sent`, {}, getAuthHeaders());
      toast.success('Soumission marquée comme envoyée');
      openDealDetail(selectedDeal.id);
      fetchPipeline();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };
  
  const handleMarkSubmissionAccepted = async (submissionId) => {
    try {
      await axios.post(`${API}/submissions/${submissionId}/mark-accepted`, {}, getAuthHeaders());
      toast.success('Soumission acceptée - Contrat créé');
      openDealDetail(selectedDeal.id);
      fetchPipeline();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };
  
  const handleMarkSubmissionRejected = async (submissionId) => {
    try {
      await axios.post(`${API}/submissions/${submissionId}/mark-rejected`, { reason: 'Refusé par le client' }, getAuthHeaders());
      toast.success('Soumission marquée comme rejetée');
      openDealDetail(selectedDeal.id);
      fetchPipeline();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };
  
  // Actions sur les contrats
  const handleUpdateContractStatus = async (contractId, newStatus) => {
    try {
      await axios.patch(`${API}/contracts/${contractId}/status`, { status: newStatus }, getAuthHeaders());
      toast.success(`Contrat mis à jour: ${CONTRACT_STATUS_CONFIG[newStatus]?.label}`);
      openDealDetail(selectedDeal.id);
      fetchPipeline();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du contrat');
    }
  };

  const formatCurrency = useCallback((value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD' }).format(value);
  }, []);

  const formatDate = useCallback((dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-CA');
  }, []);

  // Calcul du total pipeline (mémorisé pour éviter recalculs inutiles)
  const { totalPipelineValue, totalDeals, inProgressCount, wonCount } = useMemo(() => {
    const stages = Object.values(pipelineData);
    return {
      totalPipelineValue: stages.reduce((sum, stage) => sum + (stage.total_value || 0), 0),
      totalDeals: stages.reduce((sum, stage) => sum + (stage.count || 0), 0),
      inProgressCount: (pipelineData.lead?.count || 0) + (pipelineData.qualified?.count || 0) + 
                       (pipelineData.estimate?.count || 0) + (pipelineData.negotiation?.count || 0),
      wonCount: (pipelineData.won?.count || 0) + (pipelineData.production?.count || 0) + 
                (pipelineData.delivered?.count || 0)
    };
  }, [pipelineData]);

  if (loading && !user) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F5F5F5' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{ borderColor: '#335C2E' }}></div>
          <p className="mt-4" style={{ color: '#666666' }}>Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#F5F5F5' }}>
      {/* Header */}
      <header className="shadow-sm" style={{ background: '#FFFFFF' }}>
        <div className="max-w-full mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/logo.png" alt="Refacing VR" className="h-10" onError={(e) => e.target.style.display = 'none'} />
            <h1 className="text-xl font-bold" style={{ color: '#335C2E' }}>Pipeline Commercial</h1>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/admin')} className="p-2 rounded hover:bg-gray-100" title="Admin">
              <Home size={20} style={{ color: '#666666' }} />
            </button>
            <button onClick={fetchPipeline} className="p-2 rounded hover:bg-gray-100" title="Rafraîchir">
              <RefreshCw size={20} style={{ color: '#666666' }} />
            </button>
            <button onClick={() => setShowFilters(!showFilters)} className="p-2 rounded hover:bg-gray-100" title="Filtres">
              <Filter size={20} style={{ color: '#666666' }} />
            </button>
            <span className="text-sm" style={{ color: '#666666' }}>{user?.username}</span>
            <button onClick={logout} className="p-2 rounded hover:bg-gray-100">
              <LogOut size={20} style={{ color: '#DC3545' }} />
            </button>
          </div>
        </div>
      </header>

      {/* Stats Summary */}
      <div className="max-w-full mx-auto px-4 py-4">
        <div className="grid grid-cols-4 gap-4 mb-4">
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="flex items-center gap-2 text-sm" style={{ color: '#666666' }}>
              <Target size={16} />
              Total Deals
            </div>
            <div className="text-2xl font-bold" style={{ color: '#335C2E' }}>{totalDeals}</div>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="flex items-center gap-2 text-sm" style={{ color: '#666666' }}>
              <DollarSign size={16} />
              Valeur Pipeline
            </div>
            <div className="text-2xl font-bold" style={{ color: '#C7A027' }}>{formatCurrency(totalPipelineValue)}</div>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="flex items-center gap-2 text-sm" style={{ color: '#666666' }}>
              <TrendingUp size={16} />
              En cours
            </div>
            <div className="text-2xl font-bold" style={{ color: '#1976D2' }}>
              {inProgressCount}
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="flex items-center gap-2 text-sm" style={{ color: '#666666' }}>
              <Target size={16} />
              Gagnés
            </div>
            <div className="text-2xl font-bold" style={{ color: '#2E7D32' }}>
              {wonCount}
            </div>
          </div>
        </div>

        {/* Pipeline Kanban */}
        <div className="overflow-x-auto pb-4">
          <div className="flex gap-3" style={{ minWidth: `${STAGE_ORDER.length * 280}px` }}>
            {STAGE_ORDER.map(stage => {
              const stageData = pipelineData[stage] || { deals: [], count: 0, total_value: 0 };
              const config = STAGE_CONFIG[stage];
              
              return (
                <div 
                  key={stage} 
                  className="flex-shrink-0 rounded-lg shadow-sm"
                  style={{ width: '270px', background: '#FFFFFF' }}
                >
                  {/* Column Header */}
                  <div 
                    className="p-3 rounded-t-lg flex items-center justify-between"
                    style={{ background: config.bg, borderBottom: `3px solid ${config.color}` }}
                  >
                    <div>
                      <span className="font-semibold" style={{ color: config.color }}>{config.label}</span>
                      <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-white" style={{ color: config.color }}>
                        {stageData.count}
                      </span>
                    </div>
                    <div className="text-xs font-medium" style={{ color: config.color }}>
                      {formatCurrency(stageData.total_value)}
                    </div>
                  </div>
                  
                  {/* Deals Cards */}
                  <div className="p-2 space-y-2 max-h-[calc(100vh-320px)] overflow-y-auto">
                    {stageData.deals?.length === 0 ? (
                      <div className="text-center py-8 text-gray-400 text-sm">
                        Aucun deal
                      </div>
                    ) : (
                      stageData.deals?.map(deal => (
                        <div 
                          key={deal.id}
                          className="p-3 rounded-lg border cursor-pointer hover:shadow-md transition-shadow"
                          style={{ borderColor: '#E5E5E5' }}
                          onClick={() => openDealDetail(deal.id)}
                        >
                          {/* Deal Title & Score */}
                          <div className="flex items-start justify-between mb-2">
                            <h4 className="font-medium text-sm" style={{ color: '#333333' }}>
                              {deal.title || 'Sans titre'}
                            </h4>
                            <span 
                              className="px-1.5 py-0.5 rounded text-xs font-bold"
                              style={{ 
                                background: deal.score >= 50 ? '#E8F5E9' : deal.score >= 25 ? '#FFF3E0' : '#FFEBEE',
                                color: deal.score >= 50 ? '#2E7D32' : deal.score >= 25 ? '#E65100' : '#C62828'
                              }}
                            >
                              {deal.score}
                            </span>
                          </div>
                          
                          {/* Contact Info */}
                          <div className="text-xs mb-2" style={{ color: '#666666' }}>
                            <div className="flex items-center gap-1">
                              <User size={12} />
                              {deal.contact_name || 'Contact inconnu'}
                            </div>
                            {deal.project_city && (
                              <div className="flex items-center gap-1 mt-1">
                                <MapPin size={12} />
                                {deal.project_city}
                              </div>
                            )}
                          </div>
                          
                          {/* Value & Type */}
                          <div className="flex items-center justify-between mb-2">
                            <span 
                              className="px-2 py-0.5 rounded text-xs"
                              style={{ background: '#F5F5F5', color: '#666666' }}
                            >
                              {deal.deal_type?.toUpperCase() || 'N/A'}
                            </span>
                            {(deal.contract_value || deal.won_value || deal.estimated_value) > 0 && (
                              <span className="text-xs font-semibold" style={{ color: '#C7A027' }}>
                                {formatCurrency(deal.contract_value || deal.won_value || deal.estimated_value)}
                              </span>
                            )}
                          </div>
                          
                          {/* Badges Soumission & Contrat (Vague 4) */}
                          <div className="flex flex-wrap gap-1 mb-2">
                            {deal.active_submission_id && (
                              <span 
                                className="px-1.5 py-0.5 rounded text-xs"
                                style={{ 
                                  background: '#E3F2FD',
                                  color: '#1976D2'
                                }}
                                title="Soumission active"
                              >
                                📄 Soumission
                              </span>
                            )}
                            {deal.contract_id && (
                              <span 
                                className="px-1.5 py-0.5 rounded text-xs"
                                style={{ 
                                  background: '#E8F5E9',
                                  color: '#2E7D32'
                                }}
                                title="Contrat lié"
                              >
                                📝 Contrat
                              </span>
                            )}
                          </div>
                          
                          {/* Quick Actions */}
                          <div className="mt-2 pt-2 border-t flex gap-1">
                            {stage !== 'lost' && stage !== 'delivered' && (
                              <>
                                {/* Bouton Reculer - visible si pas au premier stage */}
                                {STAGE_ORDER.indexOf(stage) > 0 && stage !== 'won' && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const prevStage = STAGE_ORDER[STAGE_ORDER.indexOf(stage) - 1];
                                      handleStageChange(deal.id, prevStage);
                                    }}
                                    className="py-1 px-2 rounded text-xs flex items-center justify-center gap-1 hover:bg-gray-100"
                                    style={{ color: '#666666' }}
                                    title={`Revenir à ${STAGE_CONFIG[STAGE_ORDER[STAGE_ORDER.indexOf(stage) - 1]]?.label}`}
                                  >
                                    ← Reculer
                                  </button>
                                )}
                                {/* Bouton Avancer */}
                                {STAGE_ORDER.indexOf(stage) < STAGE_ORDER.length - 2 && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const nextStage = STAGE_ORDER[STAGE_ORDER.indexOf(stage) + 1];
                                      if (nextStage !== 'lost') {
                                        handleStageChange(deal.id, nextStage);
                                      }
                                    }}
                                    className="flex-1 py-1 rounded text-xs flex items-center justify-center gap-1 hover:bg-gray-100"
                                    style={{ color: '#335C2E' }}
                                    title={`Passer à ${STAGE_CONFIG[STAGE_ORDER[STAGE_ORDER.indexOf(stage) + 1]]?.label}`}
                                  >
                                    Avancer →
                                  </button>
                                )}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleStageChange(deal.id, 'lost');
                                  }}
                                  className="py-1 px-2 rounded text-xs hover:bg-red-50"
                                  style={{ color: '#DC3545' }}
                                  title="Marquer comme perdu"
                                >
                                  Perdu
                                </button>
                              </>
                            )}
                            {/* Pour les deals gagnés, permettre de revenir en négociation ou passer à production */}
                            {stage === 'won' && (
                              <>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleStageChange(deal.id, 'negotiation');
                                  }}
                                  className="py-1 px-2 rounded text-xs hover:bg-gray-100"
                                  style={{ color: '#666666' }}
                                  title="Revenir en négociation"
                                >
                                  ← Négociation
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleStageChange(deal.id, 'production');
                                  }}
                                  className="flex-1 py-1 rounded text-xs flex items-center justify-center gap-1 hover:bg-gray-100"
                                  style={{ color: '#4FC3F7' }}
                                  title="Passer en production"
                                >
                                  Production →
                                </button>
                              </>
                            )}
                            {/* Pour la production, permettre de revenir en won ou passer à livré */}
                            {stage === 'production' && (
                              <>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleStageChange(deal.id, 'won');
                                  }}
                                  className="py-1 px-2 rounded text-xs hover:bg-gray-100"
                                  style={{ color: '#666666' }}
                                  title="Revenir à Gagné"
                                >
                                  ← Gagné
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleStageChange(deal.id, 'delivered');
                                  }}
                                  className="flex-1 py-1 rounded text-xs flex items-center justify-center gap-1 hover:bg-gray-100"
                                  style={{ color: '#2E7D32' }}
                                  title="Marquer comme livré"
                                >
                                  Livré ✓
                                </button>
                              </>
                            )}
                            {/* Pour les deals livrés, permettre de revenir en production */}
                            {stage === 'delivered' && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleStageChange(deal.id, 'production');
                                }}
                                className="py-1 px-2 rounded text-xs hover:bg-gray-100"
                                style={{ color: '#666666' }}
                                title="Revenir en production"
                              >
                                ← Production
                              </button>
                            )}
                            {/* Pour les deals perdus, permettre de réactiver */}
                            {stage === 'lost' && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleStageChange(deal.id, 'lead');
                                }}
                                className="py-1 px-2 rounded text-xs hover:bg-blue-50"
                                style={{ color: '#2196F3' }}
                                title="Réactiver ce deal"
                              >
                                ↻ Réactiver
                              </button>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Deal Detail Modal */}
      {showDealModal && selectedDeal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-4 border-b flex items-center justify-between" style={{ background: '#335C2E' }}>
              <h2 className="text-lg font-bold text-white">{selectedDeal.title}</h2>
              <button onClick={() => setShowDealModal(false)} className="text-white hover:bg-white/20 rounded p-1">
                ✕
              </button>
            </div>
            
            {/* Tabs */}
            <div className="flex border-b">
              <button
                onClick={() => setActiveTab('info')}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'info' ? 'border-green-600 text-green-700' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Informations
              </button>
              <button
                onClick={() => setActiveTab('submissions')}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-1 ${
                  activeTab === 'submissions' ? 'border-green-600 text-green-700' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <FileText size={14} />
                Soumissions ({dealSubmissions.length})
              </button>
              <button
                onClick={() => setActiveTab('contract')}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-1 ${
                  activeTab === 'contract' ? 'border-green-600 text-green-700' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <CheckCircle size={14} />
                Contrat {dealContract ? '✓' : ''}
              </button>
            </div>
            
            {/* Content */}
            <div className="p-6 overflow-y-auto flex-1">
              {/* TAB: Informations */}
              {activeTab === 'info' && (
                <>
                  {/* Stage & Score */}
                  <div className="flex items-center gap-4 mb-6">
                    <span 
                      className="px-3 py-1 rounded-full font-medium"
                      style={{ 
                        background: STAGE_CONFIG[selectedDeal.stage]?.bg,
                        color: STAGE_CONFIG[selectedDeal.stage]?.color
                      }}
                    >
                      {STAGE_CONFIG[selectedDeal.stage]?.label}
                    </span>
                    <span className="text-sm" style={{ color: '#666666' }}>
                      Score: <strong style={{ color: selectedDeal.score >= 50 ? '#2E7D32' : '#E65100' }}>{selectedDeal.score}/100</strong>
                    </span>
                    <span className="text-sm" style={{ color: '#666666' }}>
                      Probabilité: <strong>{selectedDeal.probability}%</strong>
                    </span>
                  </div>
                  
                  {/* Info Grid */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div>
                      <label className="text-xs font-medium" style={{ color: '#999999' }}>Type de projet</label>
                      <p className="font-medium">{selectedDeal.deal_type?.toUpperCase()}</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium" style={{ color: '#999999' }}>Valeur estimée</label>
                      <p className="font-medium" style={{ color: '#C7A027' }}>{formatCurrency(selectedDeal.estimated_value)}</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium" style={{ color: '#999999' }}>Valeur gagnée</label>
                      <p className="font-medium" style={{ color: '#2E7D32' }}>{formatCurrency(selectedDeal.won_value) || '-'}</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium" style={{ color: '#999999' }}>Source</label>
                      <p className="font-medium">{selectedDeal.source || '-'}</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium" style={{ color: '#999999' }}>Ville projet</label>
                      <p className="font-medium">{selectedDeal.project_city || '-'}</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium" style={{ color: '#999999' }}>Créé le</label>
                      <p className="font-medium">{formatDate(selectedDeal.created_at)}</p>
                    </div>
                  </div>
                  
                  {/* Contact Info */}
                  {selectedDeal.contact && (
                    <div className="mb-6 p-4 rounded-lg" style={{ background: '#F5F5F5' }}>
                      <h3 className="font-semibold mb-2 flex items-center gap-2">
                        <User size={16} />
                        Contact
                      </h3>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div><strong>Nom:</strong> {selectedDeal.contact.prenom} {selectedDeal.contact.nom}</div>
                        <div><strong>Email:</strong> {selectedDeal.contact.email}</div>
                        <div><strong>Téléphone:</strong> {selectedDeal.contact.telephone || '-'}</div>
                        <div><strong>Statut:</strong> {selectedDeal.contact.statut}</div>
                      </div>
                    </div>
                  )}
                  
                  {/* Constraints */}
                  <div className="mb-6">
                    <h3 className="font-semibold mb-2">Qualifications</h3>
                    <div className="grid grid-cols-2 gap-2">
                      {['terrain_ok', 'budget_known', 'timeline_known', 'financing_ok'].map(key => (
                        <div key={key} className="flex items-center gap-2">
                          <span className={`w-4 h-4 rounded ${selectedDeal.constraints?.[key] ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                          <span className="text-sm">
                            {key === 'terrain_ok' && 'Terrain validé'}
                            {key === 'budget_known' && 'Budget connu'}
                            {key === 'timeline_known' && 'Échéancier connu'}
                            {key === 'financing_ok' && 'Financement OK'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Change Stage */}
                  <div className="mb-6">
                    <h3 className="font-semibold mb-2">Changer l'étape</h3>
                    <div className="flex flex-wrap gap-2">
                      {STAGE_ORDER.filter(s => s !== selectedDeal.stage).map(stage => (
                        <button
                          key={stage}
                          onClick={async () => {
                            await handleStageChange(selectedDeal.id, stage);
                            openDealDetail(selectedDeal.id);
                          }}
                          className="px-3 py-1.5 rounded text-xs font-medium transition-all hover:scale-105"
                          style={{ 
                            background: STAGE_CONFIG[stage]?.bg,
                            color: STAGE_CONFIG[stage]?.color,
                            border: `1px solid ${STAGE_CONFIG[stage]?.color}`
                          }}
                        >
                          {STAGE_CONFIG[stage]?.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => navigate(`/admin/crm?contact=${selectedDeal.contact_id}`)}
                      className="flex-1 py-2 rounded flex items-center justify-center gap-2"
                      style={{ background: '#335C2E', color: 'white' }}
                    >
                      <User size={16} />
                      Voir le contact
                    </button>
                  </div>
                </>
              )}
              
              {/* TAB: Soumissions */}
              {activeTab === 'submissions' && (
                <>
                  <div className="mb-4">
                    <h3 className="font-semibold">Soumission initiale</h3>
                    <p className="text-xs text-gray-500">Les modifications se font sur le contrat</p>
                  </div>
                  
                  {dealSubmissions.length === 0 ? (
                    <div className="text-center py-8" style={{ color: '#999999' }}>
                      <FileText size={48} className="mx-auto mb-2 opacity-50" />
                      <p>Aucune soumission pour ce deal</p>
                      <p className="text-xs mt-2">La soumission est créée par le prospect via le formulaire public</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {dealSubmissions.map(sub => (
                        <div 
                          key={sub.id} 
                          className="p-4 rounded-lg border"
                          style={{ 
                            borderColor: sub.id === selectedDeal.active_submission_id ? '#335C2E' : '#E5E5E5',
                            background: sub.id === selectedDeal.active_submission_id ? '#F0F7F0' : 'white'
                          }}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <span className="font-semibold">Version {sub.version}</span>
                              {sub.id === selectedDeal.active_submission_id && (
                                <span className="ml-2 px-2 py-0.5 rounded text-xs bg-green-100 text-green-800">Active</span>
                              )}
                            </div>
                            <span 
                              className="px-2 py-0.5 rounded text-xs font-medium"
                              style={{ 
                                background: SUBMISSION_STATUS_CONFIG[sub.status]?.bg,
                                color: SUBMISSION_STATUS_CONFIG[sub.status]?.color
                              }}
                            >
                              {SUBMISSION_STATUS_CONFIG[sub.status]?.label}
                            </span>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-2 text-sm mb-3" style={{ color: '#666666' }}>
                            <div>Montant: <strong style={{ color: '#C7A027' }}>{formatCurrency(sub.amount_total)}</strong></div>
                            <div>Créée le: {formatDate(sub.created_at)}</div>
                            {sub.sent_at && <div>Envoyée le: {formatDate(sub.sent_at)}</div>}
                            {sub.accepted_at && <div>Acceptée le: {formatDate(sub.accepted_at)}</div>}
                            {sub.rejected_at && <div>Rejetée le: {formatDate(sub.rejected_at)}</div>}
                          </div>
                          
                          {/* Actions */}
                          <div className="flex flex-wrap gap-2">
                            <button
                              onClick={() => navigate(`/result/${sub.id}`)}
                              className="px-3 py-1 rounded text-xs flex items-center gap-1"
                              style={{ background: '#F5F5F5', color: '#333' }}
                            >
                              <Eye size={12} />
                              Voir
                            </button>
                            
                            {sub.status === 'draft' && (
                              <button
                                onClick={() => handleMarkSubmissionSent(sub.id)}
                                className="px-3 py-1 rounded text-xs flex items-center gap-1"
                                style={{ background: '#E3F2FD', color: '#1976D2' }}
                              >
                                <Send size={12} />
                                Marquer envoyée
                              </button>
                            )}
                            
                            {sub.status === 'sent' && (
                              <>
                                <button
                                  onClick={() => handleMarkSubmissionAccepted(sub.id)}
                                  className="px-3 py-1 rounded text-xs flex items-center gap-1"
                                  style={{ background: '#E8F5E9', color: '#2E7D32' }}
                                >
                                  <CheckCircle size={12} />
                                  Acceptée
                                </button>
                                <button
                                  onClick={() => handleMarkSubmissionRejected(sub.id)}
                                  className="px-3 py-1 rounded text-xs flex items-center gap-1"
                                  style={{ background: '#FFEBEE', color: '#C62828' }}
                                >
                                  <XCircle size={12} />
                                  Rejetée
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
              
              {/* TAB: Contrat */}
              {activeTab === 'contract' && (
                <>
                  {!dealContract ? (
                    <div className="text-center py-8" style={{ color: '#999999' }}>
                      <FileText size={48} className="mx-auto mb-2 opacity-50" />
                      <p className="mb-2">Aucun contrat pour ce deal</p>
                      <p className="text-sm">Un contrat sera créé automatiquement lorsqu'une soumission sera acceptée.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Status */}
                      <div className="flex items-center justify-between p-4 rounded-lg" style={{ background: '#F5F5F5' }}>
                        <div>
                          <label className="text-xs" style={{ color: '#999999' }}>Statut actuel</label>
                          <p className="font-semibold flex items-center gap-2">
                            <span 
                              className="px-2 py-0.5 rounded text-sm"
                              style={{ 
                                background: CONTRACT_STATUS_CONFIG[dealContract.status]?.bg,
                                color: CONTRACT_STATUS_CONFIG[dealContract.status]?.color
                              }}
                            >
                              {CONTRACT_STATUS_CONFIG[dealContract.status]?.label}
                            </span>
                          </p>
                        </div>
                        <div className="text-right">
                          <label className="text-xs" style={{ color: '#999999' }}>Valeur totale</label>
                          <p className="font-bold text-lg" style={{ color: '#C7A027' }}>
                            {formatCurrency(dealContract.value_total)}
                          </p>
                        </div>
                      </div>
                      
                      {/* Dates */}
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <label className="text-xs" style={{ color: '#999999' }}>Créé le</label>
                          <p className="font-medium">{formatDate(dealContract.created_at)}</p>
                        </div>
                        {dealContract.signed_at && (
                          <div>
                            <label className="text-xs" style={{ color: '#999999' }}>Signé le</label>
                            <p className="font-medium">{formatDate(dealContract.signed_at)}</p>
                          </div>
                        )}
                        {dealContract.delivered_at && (
                          <div>
                            <label className="text-xs" style={{ color: '#999999' }}>Livré le</label>
                            <p className="font-medium">{formatDate(dealContract.delivered_at)}</p>
                          </div>
                        )}
                      </div>
                      
                      {/* Customer */}
                      <div className="p-4 rounded-lg border">
                        <h4 className="font-semibold mb-2">Client</h4>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>{dealContract.customer?.first_name} {dealContract.customer?.last_name}</div>
                          <div>{dealContract.customer?.email}</div>
                          <div>{dealContract.customer?.phone}</div>
                          <div>{dealContract.customer?.city}</div>
                        </div>
                      </div>
                      
                      {/* Change Status */}
                      <div>
                        <h4 className="font-semibold mb-2">Changer le statut</h4>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(CONTRACT_STATUS_CONFIG)
                            .filter(([key]) => key !== dealContract.status)
                            .map(([key, config]) => (
                              <button
                                key={key}
                                onClick={() => handleUpdateContractStatus(dealContract.id, key)}
                                className="px-3 py-1.5 rounded text-xs font-medium transition-all hover:scale-105"
                                style={{ 
                                  background: config.bg,
                                  color: config.color,
                                  border: `1px solid ${config.color}`
                                }}
                              >
                                {config.label}
                              </button>
                            ))}
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex gap-2 pt-4 border-t">
                        <button
                          onClick={() => navigate(`/contract/${dealContract.id}`)}
                          className="flex-1 py-2 rounded flex items-center justify-center gap-2"
                          style={{ background: '#C7A027', color: 'white' }}
                        >
                          <Eye size={16} />
                          Voir le contrat
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Pipeline;
